import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { 
  Container, 
  AppBar, 
  Toolbar, 
  Typography, 
  Box,
  Paper,
  IconButton,
  Button
} from '@mui/material';
import { Home, ArrowBack, ArrowForward } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
});

function MUIThemeDocsPage() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      <AppBar position="static">
        <Toolbar>
          <IconButton 
            component={Link} 
            to="/" 
            color="inherit" 
            sx={{ mr: 2 }}
          >
            <Home />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            MUI Theme Documentation
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        
        <Paper sx={{ p: 4 }}>
          <Typography variant="h3" gutterBottom>
            MUI 테마 설정
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            MUI 테마는 전체 애플리케이션의 디자인 시스템을 일관되게 관리할 수 있게 해줍니다. 
            색상, 폰트, 간격, 브레이크포인트 등을 중앙에서 설정하여 통일된 UI를 제공합니다.
          </Typography>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            기본 테마 생성
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            createTheme 함수를 사용하여 커스텀 테마를 만들고, ThemeProvider로 전체 앱에 적용합니다.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            색상 팔레트
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            MUI는 Material Design의 색상 체계를 따릅니다. 각 색상은 main, light, dark 변형을 가집니다.
          </Typography>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            기본 색상
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            • primary: 주요 색상<br/>
            • secondary: 보조 색상<br/>
            • error: 에러 색상 (빨간색)<br/>
            • warning: 경고 색상 (주황색)<br/>
            • info: 정보 색상 (파란색)<br/>
            • success: 성공 색상 (초록색)
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            텍스트 색상
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`<Typography color="primary">주요 텍스트</Typography>
<Typography color="text.secondary">보조 텍스트</Typography>
<Typography color="error">에러 텍스트</Typography>`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            간격 설정
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            spacing 설정으로 전체 앱의 간격 단위를 통일할 수 있습니다. 기본값은 8px입니다.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`const theme = createTheme({
  spacing: 8, // 기본값 8px
});

// 사용
<Box sx={{ p: 1 }}>  // padding: 8px
<Box sx={{ m: 2 }}>  // margin: 16px`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            타이포그래피
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            폰트 패밀리, 크기, 가중치 등을 설정하여 일관된 텍스트 스타일을 유지할 수 있습니다.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`const theme = createTheme({
  typography: {
    fontFamily: 'Noto Sans KR, Arial, sans-serif',
    h4: {
      fontWeight: 600,
    },
  },
});`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            다크 모드
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            palette.mode를 'dark'로 설정하면 자동으로 다크 테마가 적용됩니다.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`const theme = createTheme({
  palette: {
    mode: 'dark', // 또는 'light'
  },
});`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            브레이크포인트
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            반응형 디자인을 위한 화면 크기 기준점입니다. Grid 컴포넌트나 sx prop에서 활용됩니다.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            • xs: 0px 이상<br/>
            • sm: 600px 이상<br/>
            • md: 900px 이상<br/>
            • lg: 1200px 이상<br/>
            • xl: 1536px 이상
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`<Grid item xs={12} sm={6} md={4}>
  {/* 반응형 레이아웃 */}
</Grid>`}
            </Typography>
          </Paper>

        </Paper>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
          <Button 
            component={Link}
            to="/docs/mui-basics"
            variant="outlined"
            startIcon={<ArrowBack />}
          >
            Previous: Basics
          </Button>
          <Button 
            component={Link}
            to="/docs/mui-layout"
            variant="contained"
            endIcon={<ArrowForward />}
          >
            Next: Layout
          </Button>
        </Box>

      </Container>
    </ThemeProvider>
  );
}

export default MUIThemeDocsPage;
